import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import api from '../../utils/axios';

interface StateItem {
  id: number;
  name: string;
  code2: string;
  allowed: boolean;
}

const CountryList = () => {
  const [countrys, setStates] = useState<StateItem[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchStates = async () => {
    try {
      const response = await api.get('/getcountryList');
      if (response.data) {
        setStates(response.data.data);
      } else {
        toast.error('Failed to fetch countries.');
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Error fetching countries.');
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  const toggleAllowed = async (state: StateItem) => {
    try {
      const updatedState = { ...state, allowed: !state.allowed };
      const response = await api.post(
        `/updateCountry/${state.id}`,
        updatedState
      );

      if (response.data.status) {
        toast.success('Country updated successfully!');
        setStates((prev) =>
          prev.map((s) => (s.id === state.id ? { ...s, allowed: !s.allowed } : s))
        );
      } else {
        toast.error('Failed to update country.');
      }
    } catch (error) {
      console.error('Error updating country:', error);
      toast.error('Error updating country.');
    }
  };

  const filteredCountries = countrys.filter(
    (country) =>
      country?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country?.code2?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Country Restrictions</h1>
        <p className="text-sm text-gray-500">
          Configure allowed and restricted countries for access control
        </p>
      </div>

      {/* ================= SEARCH & LIST CARD ================= */}
      <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
        {/* 🔍 Search Input */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <input
            type="text"
            placeholder="Search by country name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full lg:w-1/3 px-4 py-2 border rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-50/50"
          />
        </div>

        {/* 🌍 Display Countries */}
        <div className="overflow-x-auto border rounded-lg">
          {filteredCountries.length > 0 ? (
            <table className="w-full text-sm text-left text-gray-800">
              <thead className="bg-gray-50 text-gray-600 font-semibold border-b">
                <tr>
                  <th className="px-4 py-3 w-16 text-center">S.No.</th>
                  <th className="px-4 py-3">Country Name</th>
                  <th className="px-4 py-3 w-40">Country Code</th>
                  <th className="px-4 py-3 w-32 text-center">Allowed</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredCountries.map((state, index) => (
                  <tr key={state.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-center font-medium text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{state.name}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{state.code2}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={state.allowed}
                            onChange={() => toggleAllowed(state)}
                            className="sr-only peer"
                          />
                          <div
                            className={`w-11 h-6 rounded-full relative transition-colors duration-200 ease-in-out ${
                              state.allowed ? 'bg-green-500' : 'bg-slate-300'
                            }`}
                          >
                            <div
                              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ease-in-out ${
                                state.allowed ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            ></div>
                          </div>
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-12 text-center text-gray-500">
              {countrys.length === 0 ? 'No countries available.' : 'No countries match your search query.'}
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default CountryList;
